import { useState, useEffect, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  UserCircleIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { createPersonAsync } from '../store/peopleSlice';
import { Person } from '../types';
import { peopleApi, AzureAdUserSearchResult } from '../services/api';
import Modal from './Modal';
import Button from './Button';

interface AzureAdUserSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPersonAdded?: (person: Person) => void;
}

export default function AzureAdUserSearchModal({
  isOpen,
  onClose,
  onPersonAdded,
}: AzureAdUserSearchModalProps) {
  const dispatch = useAppDispatch();
  const locations = useAppSelector((state) => state.locations.locations);
  const existingPeople = useAppSelector((state) => state.people.people);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<AzureAdUserSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<AzureAdUserSearchResult | null>(null);
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [isAdding, setIsAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setSearchResults([]);
      setSearchError(null);
      setSelectedUser(null);
      setSelectedLocationId('');
      setAddError(null);
    }
  }, [isOpen]);

  const handleSearch = useCallback(async () => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      setSearchError('Please enter at least 2 characters');
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    setSelectedUser(null);

    try {
      const results = await peopleApi.searchAzureAd(searchQuery.trim());
      setSearchResults(results);

      if (results.length === 0) {
        setSearchError('No users found matching your search');
      }
    } catch (error) {
      console.error('Search error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to search Azure AD';
      setSearchError(errorMessage);
    } finally {
      setIsSearching(false);
    }
  }, [searchQuery]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSelectUser = (user: AzureAdUserSearchResult) => {
    setSelectedUser(user);
    setAddError(null);
  };

  const isUserAlreadyAdded = (azureAdId: string): boolean => {
    return existingPeople.some((p) => p.azureAdId === azureAdId && p.status === 'active');
  };

  const handleAddPerson = async () => {
    if (!selectedUser) return;

    // Check if already exists
    if (isUserAlreadyAdded(selectedUser.id)) {
      setAddError('This user has already been added to the system');
      return;
    }

    setIsAdding(true);
    setAddError(null);

    try {
      const now = new Date().toISOString();
      const newPerson: Person = {
        id: `person-${Date.now()}`,
        name: selectedUser.displayName,
        email: selectedUser.email,
        phone: selectedUser.phone,
        mobilePhone: selectedUser.mobilePhone,
        businessPhone: selectedUser.businessPhone,
        department: selectedUser.department,
        officeLocation: selectedUser.officeLocation,
        azureAdId: selectedUser.id,
        photoUrl: selectedUser.photoUrl,
        locationId: selectedLocationId || undefined,
        status: 'active',
        createdAt: now,
        updatedAt: now,
      };

      const result = await dispatch(createPersonAsync(newPerson)).unwrap();
      onPersonAdded?.(result);
      onClose();
    } catch (error) {
      console.error('Failed to add person:', error);
      setAddError(error instanceof Error ? error.message : 'Failed to add person');
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Person from Azure AD"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isAdding}>
            Cancel
          </Button>
          <Button
            onClick={handleAddPerson}
            disabled={!selectedUser || isAdding || isUserAlreadyAdded(selectedUser?.id || '')}
          >
            {isAdding ? 'Adding...' : 'Add Person'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Search Input */}
        <div>
          <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
            Search Azure AD Users
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search by name or email..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 placeholder-secondary-400 dark:placeholder-secondary-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                disabled={isSearching}
              />
              <MagnifyingGlassIcon className="absolute left-3 top-2.5 w-5 h-5 text-secondary-400 dark:text-secondary-500" />
            </div>
            <Button onClick={handleSearch} disabled={isSearching || !searchQuery.trim()}>
              {isSearching ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                'Search'
              )}
            </Button>
          </div>
        </div>

        {/* Search Error */}
        {searchError && (
          <div className="p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <p className="text-sm text-yellow-700 dark:text-yellow-300">{searchError}</p>
          </div>
        )}

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="border border-secondary-200 dark:border-secondary-600 rounded-lg overflow-hidden">
            <div className="max-h-60 overflow-y-auto">
              {searchResults.map((user) => {
                const alreadyAdded = isUserAlreadyAdded(user.id);
                const isSelected = selectedUser?.id === user.id;

                return (
                  <button
                    key={user.id}
                    onClick={() => !alreadyAdded && handleSelectUser(user)}
                    disabled={alreadyAdded}
                    className={`w-full flex items-center p-3 text-left border-b border-secondary-100 dark:border-secondary-700 last:border-b-0 transition-colors ${
                      alreadyAdded
                        ? 'bg-secondary-50 dark:bg-secondary-800 cursor-not-allowed opacity-60'
                        : isSelected
                        ? 'bg-primary-50 dark:bg-primary-900/30 border-l-4 border-l-primary-500'
                        : 'hover:bg-secondary-50 dark:hover:bg-secondary-700'
                    }`}
                  >
                    {user.photoUrl ? (
                      <img
                        src={user.photoUrl}
                        alt={user.displayName}
                        className="w-10 h-10 rounded-full mr-3 flex-shrink-0 object-cover"
                      />
                    ) : (
                      <UserCircleIcon className="w-10 h-10 text-secondary-400 dark:text-secondary-500 mr-3 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-secondary-900 dark:text-secondary-100 truncate">
                        {user.displayName}
                      </p>
                      <p className="text-xs text-secondary-500 dark:text-secondary-400 truncate">{user.email}</p>
                      {user.department && (
                        <p className="text-xs text-secondary-400 dark:text-secondary-500">{user.department}</p>
                      )}
                    </div>
                    {alreadyAdded ? (
                      <span className="text-xs text-secondary-500 dark:text-secondary-400 bg-secondary-100 dark:bg-secondary-700 px-2 py-1 rounded">
                        Already added
                      </span>
                    ) : isSelected ? (
                      <CheckIcon className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Selected User Details */}
        {selectedUser && !isUserAlreadyAdded(selectedUser.id) && (
          <div className="p-4 bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800 rounded-lg">
            <h4 className="text-sm font-medium text-secondary-900 dark:text-secondary-100 mb-3">
              Selected User
            </h4>
            <dl className="space-y-1 text-sm">
              <div className="flex">
                <dt className="w-24 text-secondary-600 dark:text-secondary-400">Name:</dt>
                <dd className="text-secondary-900 dark:text-secondary-100">{selectedUser.displayName}</dd>
              </div>
              <div className="flex">
                <dt className="w-24 text-secondary-600 dark:text-secondary-400">Email:</dt>
                <dd className="text-secondary-900 dark:text-secondary-100">{selectedUser.email}</dd>
              </div>
              {selectedUser.officeLocation && (
                <div className="flex">
                  <dt className="w-24 text-secondary-600 dark:text-secondary-400">Office:</dt>
                  <dd className="text-secondary-900 dark:text-secondary-100">{selectedUser.officeLocation}</dd>
                </div>
              )}
              {selectedUser.department && (
                <div className="flex">
                  <dt className="w-24 text-secondary-600 dark:text-secondary-400">Department:</dt>
                  <dd className="text-secondary-900 dark:text-secondary-100">{selectedUser.department}</dd>
                </div>
              )}
              {selectedUser.businessPhone && (
                <div className="flex">
                  <dt className="w-24 text-secondary-600 dark:text-secondary-400">Bus. Phone:</dt>
                  <dd className="text-secondary-900 dark:text-secondary-100">{selectedUser.businessPhone}</dd>
                </div>
              )}
              {selectedUser.mobilePhone && (
                <div className="flex">
                  <dt className="w-24 text-secondary-600 dark:text-secondary-400">Mobile:</dt>
                  <dd className="text-secondary-900 dark:text-secondary-100">{selectedUser.mobilePhone}</dd>
                </div>
              )}
            </dl>

            {/* Location Selection */}
            <div className="mt-4 pt-4 border-t border-primary-200 dark:border-primary-700">
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                Assign to Location
              </label>
              <select
                value={selectedLocationId}
                onChange={(e) => setSelectedLocationId(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">No location assigned</option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Add Error */}
        {addError && (
          <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-700 dark:text-red-300">{addError}</p>
          </div>
        )}
      </div>
    </Modal>
  );
}
